import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Divider,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Card,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import moment from "moment";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import { AxiosError } from "axios";
import { VisibilityOutlined } from "@mui/icons-material";
import MUIDataTable from "mui-datatables";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { formatMoney } from "utils/money";
import {
  waitForSubscriptionPaymentStatus,
  SUBSCRIPTION_PAYMENT_PENDING_MESSAGE,
} from "utils/waitForSubscriptionPaymentStatus";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

interface Asset {
  last_visited: string | number | Date;
  asset_service_id: string;
  asset_type_name: string;
  asset_name: string;
  state_name: string;
  city_name: string;
  region_name: string;
  address_type: string;
  address: string;
  customer_name: string;
  customer_phone: string;
  type: string;
  subscription_creation_date: string | Date;
  subscription_type: string;
  subscription_start_date: string | Date;
  subscription_end_date: string | Date;
  asset_image: string;
  address_id: string;
  customer_id: string;
  asset_purchased_date: string | number | Date;
  asset_type_id: string;
  asset_id: string;
  image_type: string;
  division_name: string;
  asset_sku: string;
}

interface Subscription {
  subscription_id?: number; // Added for unique key
  subscription_type_name: string;
  created_at: string | Date;
  end_date: string | Date;
  is_active: boolean;
}

interface Ticket {
  ticket_id: number;
  title: string;
  ticket_service_id: string;
  customer_name: string;
  asset_name: string;
  customer_address: string;
  assignee: string;
  status_name: string;
  created_at: string | Date;
}

// Extracted component for address tooltip
interface AddressTooltipCellProps {
  value: string;
}

const AddressTooltipCell: React.FC<AddressTooltipCellProps> = ({ value }) => {
  return (
    <Tooltip title={value}>
      <span>{value.length > 20 ? `${value.substring(0, 20)}...` : value}</span>
    </Tooltip>
  );
};

// Extracted component for action buttons
interface ActionButtonCellProps {
  ticketId: number;
  onNavigate: (ticketId: number) => void;
}

const ActionButtonCell: React.FC<ActionButtonCellProps> = ({
  ticketId,
  onNavigate,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Tooltip title="View Ticket">
        <IconButton aria-label="view" onClick={() => onNavigate(ticketId)}>
          <VisibilityOutlined />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

interface PlanCardProps {
  plan: SubscriptionPlan;
  priceRow: AssetSubscriptionPrice | null | undefined;
  formatter: Intl.NumberFormat;
  durationDays: number;
  isCurrentPlan: boolean;
  payingPlanId: number | null;
  onSelectPlan: (plan: SubscriptionPlan) => void;
  activeSubscription: Subscription | null | undefined;
}

const getPlanCardBorderColor = (
  isCurrentPlan: boolean,
  isProcessing: boolean
) => {
  if (isCurrentPlan) return "success.main";
  if (isProcessing) return "primary.main";
  return "divider";
};

const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  priceRow,
  formatter,
  durationDays,
  isCurrentPlan,
  payingPlanId,
  onSelectPlan,
  activeSubscription,
}) => {
  const listPrice = Number(plan.price || 0);
  const resolvedFinal = priceRow ? Number(priceRow.final_price) : listPrice;
  const showOffer = Boolean(priceRow?.has_custom_offer);
  const originalForOffer = priceRow ? Number(priceRow.original_price) : listPrice;
  const savings =
    showOffer && originalForOffer > resolvedFinal
      ? originalForOffer - resolvedFinal
      : 0;
  const cardBg = isCurrentPlan
    ? (theme: Theme) => theme.palette.success.light + "11"
    : "transparent";
  const isProcessing = payingPlanId === plan.subscription_type_id;
  const borderColor = getPlanCardBorderColor(isCurrentPlan, isProcessing);
  const offerExpiresLabel = priceRow?.offer_expires_at
    ? moment(priceRow.offer_expires_at).format("DD MMM YYYY  h:mm A")
    : null;

  return (
    <Grid item xs={12} md={4} key={plan.subscription_type_id}>
      <Card
        variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          p: 2,
          borderColor,
          backgroundColor: cardBg,
        }}
      >
        <Typography variant="h6">{plan.subscription_type_name}</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1 }}
        >
          {plan.description || "No description"}
        </Typography>
        {showOffer ? (
          <Box
            sx={{
              mt: "auto",
              p: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "warning.main",
              bgcolor: (theme) => theme.palette.warning.light + "22",
            }}
          >
            <Typography
              variant="subtitle2"
              color="warning.dark"
              fontWeight="bold"
              sx={{ mb: 1 }}
            >
              Special offer active!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Standard price:{" "}
              <Box
                component="span"
                sx={{ textDecoration: "line-through" }}
              >
                {formatter.format(originalForOffer)}
              </Box>
            </Typography>
            <Typography variant="h6" sx={{ mt: 0.5 }}>
              Your price:{" "}
              <Box component="span" color="primary.main">
                {formatter.format(resolvedFinal)}
              </Box>
              {savings > 0 && (
                <Typography
                  component="span"
                  variant="body2"
                  color="success.main"
                  sx={{ ml: 1 }}
                >
                  (save {formatter.format(savings)})
                </Typography>
              )}
            </Typography>
            {offerExpiresLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 1 }}
              >
                Offer expires: {offerExpiresLabel}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mt: "auto" }}>
              Price
            </Typography>
            <Typography variant="h5">
              {formatter.format(resolvedFinal)}
            </Typography>
          </>
        )}
        {durationDays > 0 && (
          <Typography variant="caption" color="text.secondary">
            Duration: {durationDays} days
          </Typography>
        )}
        {isCurrentPlan ? (
          <Box
            sx={{
              mt: 2,
              py: 1,
              px: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "success.main",
              backgroundColor: (theme) =>
                theme.palette.success.main + "11",
            }}
          >
            <Typography
              variant="subtitle2"
              color="success.main"
              fontWeight="bold"
            >
              Current Plan
            </Typography>
            {activeSubscription?.end_date && (
              <Typography variant="caption" color="text.secondary">
                Active until{" "}
                {moment(activeSubscription.end_date).format("MMM D, YYYY")}
              </Typography>
            )}
          </Box>
        ) : (
          <Button
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => onSelectPlan(plan)}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              "Pay & activate"
            )}
          </Button>
        )}
      </Card>
    </Grid>
  );
};

interface SubscriptionPlan {
  subscription_type_id: number;
  subscription_type_name: string;
  description?: string | null;
  price: number | string;
  duration_in_days?: number;
}

interface AssetSubscriptionPrice {
  final_price: number;
  original_price: number;
  custom_plan_id: number | null;
  offer_expires_at: string | null;
  has_custom_offer: boolean;
  currency: string;
  effective_duration_days: number;
  gst_percent?: number;
  gst_amount?: number;
  total_amount?: number;
}

const CustomerViewAsset: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { fetchError, fetchSuccess } = useSnackbar();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const appCurrency =
    (user as any)?.client_currency ||
    (user as any)?.currency_code ||
    "USD";
  const [assetData, setAssetData] = useState<Asset | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [planPrices, setPlanPrices] = useState<
    Record<number, AssetSubscriptionPrice | null>
  >({});
  const [planPricesLoading, setPlanPricesLoading] = useState(false);
  const [payingPlanId, setPayingPlanId] = useState<number | null>(null);
  const defaultStartDate = useMemo(
    () => moment().format("YYYY-MM-DD"),
    []
  );
  const [startDateDialogOpen, setStartDateDialogOpen] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | null>(null);
  const [startDateInput, setStartDateInput] = useState(defaultStartDate);
  const [startDateError, setStartDateError] = useState<string | null>(null);
  type FutureSubscriptionConfirmPayload = {
    error: string;
    message?: string;
    existing: { start_date: string; end_date: string };
    proposed: { start_date: string; end_date: string };
  };
  const [futureSubscriptionDialog, setFutureSubscriptionDialog] = useState<null | {
    plan: SubscriptionPlan;
    selectedStartDate: string;
    payload: FutureSubscriptionConfirmPayload;
  }>(null);

  const formatSubscriptionDate = (ymd: string) => {
    try {
      return new Date(`${ymd}T12:00:00`).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return ymd;
    }
  };

  const handleMediaOpen = (fileName: string) => {
    const url = `${process.env.REACT_APP_CLOUD_FRONT_URL}/${fileName}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const activeSubscription = useMemo(() => {
    return subscriptions.find((sub) => {
      if (!sub.is_active) return false;
      if (!sub.end_date) return true;
      return moment(sub.end_date).isSameOrAfter(moment(), "day");
    });
  }, [subscriptions]);

  const loadRazorpayScript = (): Promise<void> => {
    if (typeof window !== "undefined" && (window as any).Razorpay) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay"));
      document.body.appendChild(script);
    });
  };

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadingTickets(true);
    try {
      const [assetResponse, subscriptionResponse, ticketsResponse] =
        await Promise.all([
          axios.get(`/assets/${id}`),
          axios.get(`/subscription/assets/${id}`),
          axios.get(`/tickets/asset/${id}`),
        ]);

      setAssetData(assetResponse?.data);
      setSubscriptions(subscriptionResponse?.data?.list || []);
      setTickets(ticketsResponse?.data?.list || []);
    } catch (error) {
      navigate("/customer/assets-list");
      if (error instanceof AxiosError && error.response) {
        fetchError(error.response?.data?.error || error.message);
      }
    } finally {
      setLoading(false);
      setLoadingTickets(false);
    }
  }, [id, fetchError, navigate]);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, fetchData]);

  const fetchPlans = useCallback(
    async (assetTypeId: number) => {
      setLoadingPlans(true);
      try {
        const res = await axios.get(
          `/subscription-types/assets/${assetTypeId}?is_active=1`
        );
        setPlans(res.data?.list || []);
      } catch (error) {
        if (error instanceof AxiosError && error.response) {
          fetchError(error.response?.data?.error || error.message);
        }
      } finally {
        setLoadingPlans(false);
      }
    },
    [fetchError]
  );

  useEffect(() => {
    if (assetData?.asset_type_id) {
      fetchPlans(Number(assetData.asset_type_id));
    } else {
      setPlans([]);
    }
  }, [assetData?.asset_type_id, fetchPlans]);

  useEffect(() => {
    if (!id || plans.length === 0) {
      setPlanPrices({});
      return;
    }
    let cancelled = false;
    (async () => {
      setPlanPricesLoading(true);
      try {
        const entries = await Promise.all(
          plans.map(async (p) => {
            try {
              const res = await axios.get<AssetSubscriptionPrice>(
                `/customer/assets/${id}/subscription-price`,
                { params: { subscription_type_id: p.subscription_type_id } }
              );
              return [p.subscription_type_id, res.data] as const;
            } catch {
              return [p.subscription_type_id, null] as const;
            }
          })
        );
        if (!cancelled) {
          setPlanPrices(Object.fromEntries(entries) as Record<number, AssetSubscriptionPrice | null>);
        }
      } finally {
        if (!cancelled) {
          setPlanPricesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, plans]);

  const openStartDateDialog = useCallback(
    (plan: SubscriptionPlan) => {
      setPendingPlan(plan);
      setStartDateInput(defaultStartDate);
      setStartDateError(null);
      setStartDateDialogOpen(true);
    },
    [defaultStartDate]
  );

  const closeStartDateDialog = useCallback(() => {
    if (payingPlanId) return;
    setStartDateDialogOpen(false);
    setPendingPlan(null);
    setStartDateError(null);
  }, [payingPlanId]);

  const assetTypeId = assetData?.asset_type_id;

  const effectiveDurationDays = useCallback(
    (plan: SubscriptionPlan, priceRow: AssetSubscriptionPrice | null | undefined) => {
      const fromApi = priceRow?.effective_duration_days;
      if (fromApi != null && Number(fromApi) > 0) {
        return Number(fromApi);
      }
      const fromPlan = plan.duration_in_days;
      if (fromPlan != null && Number(fromPlan) > 0) {
        return Number(fromPlan);
      }
      return 0;
    },
    []
  );

  const parseStartDateInput = (selectedStartDate: string) => {
    if (!selectedStartDate) {
      return { error: "Please select a start date." };
    }
    const parsedStart = moment(selectedStartDate, "YYYY-MM-DD", true);
    if (!parsedStart.isValid()) {
      return { error: "Start date format is invalid." };
    }
    if (parsedStart.isBefore(moment().startOf("day"))) {
      return { error: "Start date cannot be before today." };
    }
    return { parsedStart };
  };

  const getActiveSubscriptionError = (parsedStart: moment.Moment) => {
    if (!activeSubscription) return null;
    if (!activeSubscription.end_date) {
      return "An active subscription already exists for this asset.";
    }
    if (!parsedStart.isAfter(moment(activeSubscription.end_date), "day")) {
      return "An active subscription already exists for this asset.";
    }
    return null;
  };

  const buildOrderPayload = useCallback(
    async (
      plan: SubscriptionPlan,
      startYmd: string,
      confirmFuture: boolean
    ) => {
      if (!id) return null;
      const customerId = Number(assetData?.customer_id);
      if (!customerId) {
        throw new Error("Customer context missing for this asset.");
      }
      const priceRow = planPrices[plan.subscription_type_id];
      try {
        const res = await axios.post("/customer-subscriptions/create-order", {
          asset_id: Number(id),
          subscription_type_id: plan.subscription_type_id,
          customer_id: customerId,
          start_date: startYmd,
          confirm_future_subscription: confirmFuture,
          custom_plan_id: priceRow?.custom_plan_id ?? null,
          original_amount: priceRow?.original_price ?? null,
        });
        return res.data;
      } catch (orderErr: unknown) {
        if (
          orderErr instanceof AxiosError &&
          orderErr.response?.status === 409 &&
          orderErr.response?.data?.error === "FUTURE_SUBSCRIPTION_CONFIRM"
        ) {
          setFutureSubscriptionDialog({
            plan,
            selectedStartDate: startYmd,
            payload: orderErr.response
              .data as FutureSubscriptionConfirmPayload,
          });
          return null;
        }
        throw orderErr;
      }
    },
    [id, planPrices, assetData?.customer_id]
  );

  const launchRazorpayCheckout = useCallback(
    async (
      orderPayload: any,
      plan: SubscriptionPlan,
      startYmd: string,
      confirmFuture: boolean
    ) => {
      console.log("[PAYMENT_DEBUG] customer (CustomerViewAsset): opening Razorpay", {
        orderId: orderPayload?.orderId,
      });
      await loadRazorpayScript();
      const Razorpay = (window as any).Razorpay;
      if (!Razorpay) {
        throw new Error("Payment gateway could not be loaded.");
      }
      const rzp = new Razorpay({
        key: orderPayload.key_id,
        amount: Number(orderPayload.amount),
        currency: orderPayload.currency || String(appCurrency || "USD"),
        order_id: orderPayload.orderId,
        name: "Scout Track Solutions",
        description: `${plan.subscription_type_name} subscription`,
        handler: async (response: {
          razorpay_payment_id: string;
          razorpay_order_id: string;
          razorpay_signature: string;
        }) => {
          console.log("[PAYMENT_DEBUG] customer (CustomerViewAsset): Razorpay handler", {
            orderId: response.razorpay_order_id,
          });
          try {
            const verifyPayload = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              customer_id: Number(assetData?.customer_id),
              start_date: startYmd,
              confirm_future_subscription: confirmFuture,
            };
            const verifyPromise = axios.post("/customer-subscriptions/verify", verifyPayload);
            const poll = await waitForSubscriptionPaymentStatus(
              response.razorpay_order_id,
              "customer"
            );
            let verifyOk = false;
            try {
              const verifyRes = await verifyPromise;
              verifyOk = verifyRes.status === 200 || verifyRes.status === 201;
            } catch (e) {
             
              verifyOk = false;
            }
           
            if (poll.activated || verifyOk) {
              fetchSuccess("Subscription activated successfully.");
            } else {
      
              fetchSuccess(SUBSCRIPTION_PAYMENT_PENDING_MESSAGE);
            }
            await fetchData();
            if (assetTypeId) {
              await fetchPlans(Number(assetTypeId));
            }
          } catch (err: any) {
            fetchError(
              err?.response?.data?.error || "Payment verification failed."
            );
          } finally {
            setPayingPlanId(null);
          }
        },
        modal: {
          ondismiss: () => {
            setPayingPlanId(null);
          },
        },
      });
      rzp.open();
    },
    [
      appCurrency,
      assetData?.customer_id,
      assetTypeId,
      fetchData,
      fetchError,
      fetchPlans,
      fetchSuccess,
      loadRazorpayScript,
    ]
  );

  const handleSubscribe = useCallback(
    async (
      plan: SubscriptionPlan,
      selectedStartDate: string,
      confirmFuture = false
    ) => {
      if (!id) return;
      const { parsedStart, error } = parseStartDateInput(selectedStartDate);
      if (error || !parsedStart) {
        fetchError(error || "Invalid start date.");
        return;
      }
      const conflictError = getActiveSubscriptionError(parsedStart);
      if (conflictError) {
        fetchError(conflictError);
        return;
      }
      const startYmd = parsedStart.format("YYYY-MM-DD");

      setPayingPlanId(plan.subscription_type_id);
      try {
        const orderPayload = await buildOrderPayload(
          plan,
          startYmd,
          confirmFuture
        );
        if (!orderPayload) {
          setPayingPlanId(null);
          return;
        }
        await launchRazorpayCheckout(
          orderPayload,
          plan,
          startYmd,
          confirmFuture
        );
      } catch (error: any) {
        fetchError(
          error?.response?.data?.error ||
            error.message ||
            "Could not start payment."
        );
        setPayingPlanId(null);
      }
    },
    [
      id,
      fetchError,
      parseStartDateInput,
      getActiveSubscriptionError,
      buildOrderPayload,
      launchRazorpayCheckout,
    ]
  );

  const handleFutureSubscriptionConfirm = useCallback(() => {
    if (!futureSubscriptionDialog) return;
    const { plan, selectedStartDate } = futureSubscriptionDialog;
    setFutureSubscriptionDialog(null);
    handleSubscribe(plan, selectedStartDate, true).catch(() => undefined);
  }, [futureSubscriptionDialog, handleSubscribe]);

  const confirmSubscription = useCallback(async () => {
    if (!pendingPlan) return;
    if (!startDateInput) {
      setStartDateError("Start date is required.");
      return;
    }
    const parsedStart = moment(startDateInput, "YYYY-MM-DD", true);
    if (!parsedStart.isValid()) {
      setStartDateError("Start date format is invalid.");
      return;
    }
    if (parsedStart.isBefore(moment().startOf("day"))) {
      setStartDateError("Start date cannot be before today.");
      return;
    }
    setStartDateError(null);
    setStartDateDialogOpen(false);
    await handleSubscribe(pendingPlan, startDateInput);
    setPendingPlan(null);
  }, [handleSubscribe, pendingPlan, startDateInput]);

  // Fixed: Render functions for custom cells
  const renderAddressCell = (value: string) => {
    return <AddressTooltipCell value={value} />;
  };

  const renderActionCell = (ticketId: number) => {
    return (
      <ActionButtonCell
        ticketId={ticketId}
        onNavigate={(id) => navigate(`/admin/ticket-view/${id}`)}
      />
    );
  };

  // Fixed: Render tickets section with proper conditional logic
  const renderTicketsSection = () => {
    if (loadingTickets) {
      return (
        <>
          <Divider sx={{ height: 2, backgroundColor: "blue", mt: 3 }} />
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <Typography variant="body1">Loading Tickets...</Typography>
          </Box>
        </>
      );
    }

    if (tickets.length === 0) {
      return null;
    }

    return (
      <>
        <Divider sx={{ height: 2, backgroundColor: "blue", mt: 3 }} />
        <ThemeProvider theme={getMuiTheme}>
          <MUIDataTable
            title={"Tickets"}
            data={tickets.map((ticket) => ({
              ...ticket,
              title: ticket.title || "-",
              customer_name: ticket.customer_name || "-",
              asset_name: ticket.asset_name || "-",
              address: `${ticket.customer_address || ""}`,
              assignee: ticket.assignee || "Unassigned",
              created_at: moment(ticket.created_at).format("MMM D, YYYY"),
            }))}
            columns={[
              { name: "title", label: "Title" },
              { name: "ticket_service_id", label: "Service Id" },
              { name: "asset_name", label: "Asset Type" },
              {
                name: "address",
                label: "Address",
                options: {
                  customBodyRender: renderAddressCell,
                },
              },
              { name: "status_name", label: "Status" },
              { name: "assignee", label: "Assignee" },
              { name: "created_at", label: "Created At" },
              {
                name: "ticket_id",
                label: "Actions",
                options: {
                  filter: false,
                  sort: false,
                  customBodyRender: renderActionCell,
                },
              },
            ]}
            options={{
              selectableRows: "none",
              elevation: 0,
              download: true,
              print: true,
              filter: true,
              search: true,
              responsive: "standard",
            }}
          />
        </ThemeProvider>
      </>
    );
  };

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: String(appCurrency || "USD"),
        minimumFractionDigits: 2,
      }),
    [appCurrency]
  );

  const renderPlanCard = useCallback(
    (plan: SubscriptionPlan) => {
      const priceRow = planPrices[plan.subscription_type_id];
      const durationDays = effectiveDurationDays(plan, priceRow);
      const isCurrentPlan =
        !!activeSubscription &&
        activeSubscription.subscription_type_name ===
          plan.subscription_type_name;
      return (
        <PlanCard
          key={plan.subscription_type_id}
          plan={plan}
          priceRow={priceRow}
          formatter={formatter}
          durationDays={durationDays}
          isCurrentPlan={isCurrentPlan}
          payingPlanId={payingPlanId}
          onSelectPlan={openStartDateDialog}
          activeSubscription={activeSubscription}
        />
      );
    },
    [
      activeSubscription,
      effectiveDurationDays,
      formatter,
      openStartDateDialog,
      payingPlanId,
      planPrices,
    ]
  );

  const renderPlansSection = () => {
    if (loadingPlans || (plans.length > 0 && planPricesLoading)) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (plans.length === 0) {
      return (
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          No active plans are available for this asset type right now.
        </Typography>
      );
    }
    return (
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {plans.map((plan) => renderPlanCard(plan))}
      </Grid>
    );
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        className="view-ticket"
        style={{
          padding: "24px",
          width: "100%",
          borderRadius: "8px",
        }}
      >
        <Grid
          item
          xs={12}
          display={"flex"}
          justifyContent={"center"}
          className="view-ticket-title"
        >
          <IconButton className="back-btn">
            <ArrowBackIcon onClick={() => navigate("/customer/assets-list")} />
          </IconButton>
          <Typography variant="h5" gutterBottom>
            Asset Details
          </Typography>
        </Grid>
        <Grid container className="view-ticket-details">
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Asset Id</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.asset_service_id}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Brand Name</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.asset_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.customer_name}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Customer Phone</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.customer_phone}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Address</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={10} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {`${assetData?.address}, ${assetData?.city_name}, ${assetData?.state_name}, ${assetData?.address_type}`}
                </span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Subscription Type</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.subscription_type}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Subscription Start Date</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {assetData?.subscription_start_date
                    ? moment(assetData?.subscription_start_date).format(
                        "MMM D, YYYY"
                      )
                    : ""}
                </span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Subscription End Date</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :{" "}
                  {assetData?.subscription_end_date
                    ? moment(assetData?.subscription_end_date).format(
                        "MMM D, YYYY"
                      )
                    : ""}
                </span>
              </Box>
            </Typography>
          </Grid>
          {assetData?.type === "company" && (
            <>
              <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
                <Typography variant="h6">
                  <Box display="flex" justifyContent="left">
                    <span>Division</span>
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                  <Box display="flex" justifyContent="flex-start">
                    <span>: {assetData?.division_name}</span>
                  </Box>
                </Typography>
              </Grid>
            </>
          )}
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>SKU</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>: {assetData?.asset_sku}</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={4} sm={4} md={2} sx={{ my: 1 }}>
            <Typography variant="h6">
              <Box display="flex" justifyContent="left">
                <span>Last Visited On</span>
              </Box>
            </Typography>
          </Grid>
          <Grid item xs={8} sm={8} md={4} sx={{ my: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: "normal" }}>
              <Box display="flex" justifyContent="flex-start">
                <span>
                  :
                  {assetData?.last_visited
                    ? moment(assetData?.last_visited).format("MMM D, YYYY")
                    : "No Tickets"}
                </span>
              </Box>
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5">Available subscription plans</Typography>
          <Typography variant="body2" color="text.secondary">
            Pick a plan for this asset. Payments are processed securely with Razorpay.
          </Typography>
        </Box>
        {renderPlansSection()}

        <Dialog open={startDateDialogOpen} onClose={closeStartDateDialog}>
          <DialogTitle>Select Start Date</DialogTitle>
          <DialogContent sx={{ minWidth: { xs: 280, sm: 360 } }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {pendingPlan?.subscription_type_name || "Subscription"}
            </Typography>
            {pendingPlan &&
              (() => {
                const pr = planPrices[pendingPlan.subscription_type_id];
                if (!pr?.has_custom_offer) return null;
                const orig = Number(pr.original_price);
                const fin = Number(pr.final_price);
                const sav = orig > fin ? orig - fin : 0;
                return (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.25,
                      borderRadius: 1,
                      bgcolor: (theme) => theme.palette.warning.light + "18",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Offer price {formatter.format(fin)}
                      {sav > 0
                        ? ` · save ${formatter.format(sav)} vs ${formatter.format(
                            orig
                          )}`
                        : ""}
                    </Typography>
                    {pr.offer_expires_at && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Expires{" "}
                        {moment(pr.offer_expires_at).format("DD MMM YYYY h:mm A")}
                      </Typography>
                    )}
                  </Box>
                );
              })()}
            <Typography variant="caption" fontWeight="bold">
              Start Date
            </Typography>
            <TextField
              type="date"
              value={startDateInput}
              onChange={(e) => {
                setStartDateInput(e.target.value);
                setStartDateError(null);
              }}
              inputProps={{ min: defaultStartDate }}
              fullWidth
              error={Boolean(startDateError)}
              helperText={startDateError || " "}
              sx={{ mt: 0.5 }}
            />
            {pendingPlan &&
              effectiveDurationDays(
                pendingPlan,
                planPrices[pendingPlan.subscription_type_id]
              ) > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                End Date:&nbsp;
                {moment(startDateInput, "YYYY-MM-DD")
                  .add(
                    effectiveDurationDays(
                      pendingPlan,
                      planPrices[pendingPlan.subscription_type_id]
                    ) - 1,
                    "day"
                  )
                  .format("MMM D, YYYY")}
              </Typography>
            )}

            {pendingPlan &&
              (() => {
                const pr = planPrices[pendingPlan.subscription_type_id];
                if (!pr) return null;
                const currency = String(pr.currency || appCurrency || "USD");
                const subTotal = Number(pr.final_price || 0);
                const gstPercent = Number(pr.gst_percent || 0);
                const gstAmount =
                  gstPercent > 0
                    ? pr.gst_amount != null
                      ? Number(pr.gst_amount)
                      : (subTotal * gstPercent) / 100
                    : 0;
                const total =
                  pr.total_amount != null
                    ? Number(pr.total_amount)
                    : subTotal + gstAmount;

                return (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" fontWeight="bold">
                      Payment summary
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.75,
                        display: "grid",
                        gridTemplateColumns: "1fr auto",
                        rowGap: 0.5,
                        columnGap: 2,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Subtotal:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatMoney(subTotal, currency)}
                      </Typography>

                      {gstPercent > 0 ? (
                        <>
                          <Typography variant="body2" color="text.secondary">
                            TAX ({gstPercent}%):
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatMoney(gstAmount, currency)}
                          </Typography>
                        </>
                      ) : null}

                      <Typography variant="body2" color="text.secondary">
                        Total:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {formatMoney(total, currency)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeStartDateDialog} disabled={!!payingPlanId}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={confirmSubscription}
              disabled={!!payingPlanId}
            >
              Continue
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(futureSubscriptionDialog)}
          onClose={() => payingPlanId == null && setFutureSubscriptionDialog(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>New subscription after current period?</DialogTitle>
          <DialogContent>
            {futureSubscriptionDialog
              ? futureSubscriptionDialog.payload.message ??
                `Do you want a new subscription from ${formatSubscriptionDate(
                  futureSubscriptionDialog.payload.proposed.start_date
                )} to ${formatSubscriptionDate(
                  futureSubscriptionDialog.payload.proposed.end_date
                )}? Your current subscription on this asset runs until ${formatSubscriptionDate(
                  futureSubscriptionDialog.payload.existing.end_date
                )}.`
              : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setFutureSubscriptionDialog(null)}
              disabled={payingPlanId != null}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={handleFutureSubscriptionConfirm}
              disabled={payingPlanId != null}
              sx={{ textTransform: "none" }}
            >
              Yes, continue
            </Button>
          </DialogActions>
        </Dialog>

        {/* Subscription History Section */}
        {subscriptions && subscriptions.length > 0 && (
          <>
            <Divider sx={{ height: 2, backgroundColor: "blue", my: 2 }} />
            <Typography variant="h5" gutterBottom>
              Subscription History
            </Typography>
            <TableContainer component={Paper}>
              <Table>
                <TableHead sx={{ display: "table-row-group" }}>
                  <TableRow>
                    <TableCell>Subscription Type</TableCell>
                    <TableCell>Created Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Fixed: Use subscription_id or created_at for unique keys */}
                  {subscriptions.map((data, index) => (
                    <TableRow
                      key={
                        data.subscription_id || `${data.created_at}-${index}`
                      }
                    >
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                          <Box>
                            <span>{String(data?.subscription_type_name)}</span>
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                          <Box>
                            <span>
                              {moment(data?.created_at).format("MMM D, YYYY")}
                            </span>
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                          <Box>
                            <span>
                              {moment(data?.end_date).format("MMM D, YYYY")}
                            </span>
                          </Box>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: "normal" }}>
                          <Box>
                            <span>
                              {data?.is_active ? "Active" : "In-Active"}
                            </span>
                          </Box>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Fixed: Extracted nested ternary into function */}
        {renderTicketsSection()}

        {/* Asset Media Section */}
        <Divider sx={{ height: 2, backgroundColor: "blue", my: 2 }} />
        <Typography variant="h5" gutterBottom>
          Asset Media
        </Typography>
        <div
          style={{ justifyContent: "center", display: "flex" }}
          className="ticket-media"
        >
          {assetData?.asset_image ? (
            <div className="media-container view-media">
              {/* Fixed: Wrapped image in button for accessibility */}
              <button
                onClick={() => handleMediaOpen(assetData?.asset_image)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  display: "inline-block",
                }}
                aria-label="View full size asset image"
              >
                <img
                  className="upload-img"
                  src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${assetData?.asset_image}`}
                  alt="lift-media"
                  style={{ display: "block" }}
                />
              </button>
            </div>
          ) : (
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              No media available
            </Typography>
          )}
        </div>
      </Card>
    </DashboardLayout>
  );
};

export default CustomerViewAsset;
